import {
  Card,
  CardContent,
  Grid,
  Divider as MuiDivider,
  Typography as MuiTypography,
  makeStyles,
} from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import {
  fetchCountAllApplicationsKindsAndStatusesForPortAuth,
  fetchCountAllApplicationsKindsForPortAuth,
} from "../../services/applicationsService";

import { Helmet } from "react-helmet-async";
import { spacing } from "@material-ui/system";
import styled from "styled-components/macro";
import useLocale from "../../hooks/useLocale";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
  NEW_NAUTICAL_APPLICATION_CHART,
  RESIGN_NAUTICAL,
  ISSUE_COPY_NAUTICAL,
  NEW_NAUTICAL_APPLICATION,
} from "../../constants/index";
import {
  applicationBlue,
  applicationGreen,
  applicationPurple,
} from "../../theme/customColors";
import DoughnutChart from "./Default/DoughnutChart_R";
import BarChartTab from "./Default/BarChartTab";
import chroma from "chroma-js";

const Divider = styled(MuiDivider)(spacing);

const Typography = styled(MuiTypography)(spacing);

const groupBy = (input, key) => {
  return input.reduce((acc, currentValue) => {
    let groupKey = currentValue[key];
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(currentValue);
    return acc;
  }, {});
};

const useStyles = makeStyles((theme) => ({
  cardsSpace: {
    [theme.breakpoints.down("lg")]: {
      width: "100%",
    },
    [theme.breakpoints.up("lg")]: {
      width: "50%",
    },
  },
  card: {
    margin: 20,
  },
  cardDiv: {
    display: "flex",
    flexDirection: "row",
  },
  doughnutChartContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
}));

function DefaultEmployee() {
  const { t } = useLocale();
  const [kindSts, setKindSts] = useState(null);
  const [portAuthId] = useState(
    useSelector((state) => state?.authReducer?.user?.port_auth_id)
  );
  const { data: countAllApplicationsKinds } = useQuery(
    ["countAllApplicationsKindsForPortAuth", portAuthId],
    () => fetchCountAllApplicationsKindsForPortAuth(portAuthId)
  );
  const { data: countAllApplicationsKindsAndStatuses } = useQuery(
    ["countAllApplicationsKindsAndStatusesForPortAuth", portAuthId],
    () => fetchCountAllApplicationsKindsAndStatusesForPortAuth(portAuthId)
  );
  const classes = useStyles();
  const [kindsDataForPie, setKindsDataForPie] = useState([]);
  const [kindStsForBarGraph, setKindStsForBarGraph] = useState([]);
  const [tableOfExtraColors, setTableOfExtraColors] = useState([]);

  useEffect(() => {
    setKindSts(
      Object?.values(
        groupBy(countAllApplicationsKindsAndStatuses || [], "appl_kind_id")
      )
    );
  }, [countAllApplicationsKindsAndStatuses]);

  //Make a table with Colors for Dougnut Chart and Bar Chart. We use useMemo because if not we don't get consistent colros on both charts
  useMemo(() => {
    setTableOfExtraColors(
      countAllApplicationsKinds?.map((a) => ({ color: chroma.random().hex() }))
    );
  }, [countAllApplicationsKinds]);

  useEffect(() => {
    let labels = [];
    let data = [];
    let backgroundColor = [];

    let addColorData =
      //countApplKinds

      countAllApplicationsKinds?.map((appl, index) => {
        if (appl.appl_kind_id === NEW_NAUTICAL_APPLICATION_CHART) {
          return { color: applicationBlue[500], ...appl };
        } else if (appl.appl_kind_id === RESIGN_NAUTICAL) {
          return { color: applicationGreen[500], ...appl };
        } else if (appl.appl_kind_id === ISSUE_COPY_NAUTICAL) {
          return { color: applicationPurple[500], ...appl };
        } else {
          return { color: tableOfExtraColors[index]?.color, ...appl };
        }
      });

    addColorData?.forEach((appl) => {
      labels.push(appl.dscr);
      data.push(appl.count);
      backgroundColor.push(appl.color);
    });

    let ChartData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor: backgroundColor,
        },
      ],
    };

    countAllApplicationsKinds?.length > 0
      ? setKindsDataForPie(ChartData)
      : setKindsDataForPie([]);
  }, [countAllApplicationsKinds]);

  //Bar Char Data
  useEffect(() => {
    let ArrayWithObjectsForGraph = [];

    //Make a new Object with Empty Arrays which the name of each array has the appl_kind_id
    let tablesForGraphs = {};
    kindSts?.forEach((appl) => {
      tablesForGraphs[`${appl[0].appl_kind_id}`] = [];
    });

    // Add Color to the different Applications so they stay consistent with Doughnut Chart
    let kindStsWithColorAdded = kindSts?.map((status, index) => {
      let b = status.map((eachStatus) => {
        return { color: tableOfExtraColors[index]?.color, ...eachStatus };
      });
      return b;
    });

    const kindStsNew = kindStsWithColorAdded?.flat(); //Merge the kindSts Array that has arrays of status all together in a single array
    kindStsNew?.forEach((appl) => {
      if (appl.appl_kind_id === NEW_NAUTICAL_APPLICATION_CHART) {
        tablesForGraphs[NEW_NAUTICAL_APPLICATION].push({
          color: applicationBlue[500],
          title: appl.kindDesc,
          name: appl.stsDesc,
          value: appl.count,
        });
      } else if (appl.appl_kind_id === RESIGN_NAUTICAL) {
        tablesForGraphs[RESIGN_NAUTICAL].push({
          color: applicationGreen[500],
          title: appl.kindDesc,
          name: appl.stsDesc,
          value: appl.count,
        });
      } else if (appl.appl_kind_id === ISSUE_COPY_NAUTICAL) {
        tablesForGraphs[ISSUE_COPY_NAUTICAL].push({
          color: applicationPurple[500],
          title: appl.kindDesc,
          name: appl.stsDesc,
          value: appl.count,
        });
      } else {
        tablesForGraphs[`${appl.appl_kind_id}`].push({
          color: appl.color,
          title: appl.kindDesc,
          name: appl.stsDesc,
          value: appl.count,
        });
      }
    });

    //Pushes the object with data and points to the corresponding array with data associated
    Object.entries(tablesForGraphs).forEach(([key, value]) => {
      ArrayWithObjectsForGraph.push({ data: value });
    });

    kindStsNew?.length > 0
      ? setKindStsForBarGraph(ArrayWithObjectsForGraph)
      : setKindStsForBarGraph([]);
  }, [kindSts]);

  return (
    <React.Fragment>
      <Helmet title={t("Dashboard")} />
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            {t("Dashboard")}
          </Typography>
        </Grid>
      </Grid>

      <Divider my={6} />
      <Grid container direction="row">
        <Grid item className={classes.cardsSpace}>
          <Card elevation={3} className={classes.card}>
            <CardContent>
              <Typography variant="h4">{t("Application Kinds")}</Typography>
            </CardContent>
            <CardContent className={classes.doughnutChartContent}>
              {kindsDataForPie?.length === 0 ? (
                <Typography variant="caption">
                  {t("No stats available")}
                </Typography>
              ) : (
                <DoughnutChart data={kindsDataForPie} />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item className={classes.cardsSpace}>
          <Card elevation={3} className={classes.card}>
            <CardContent>
              <Typography variant="h4">
                {t("Statuses per Application Kind")}
              </Typography>
            </CardContent>
            <CardContent className={classes.doughnutChartContent}>
              {kindStsForBarGraph?.length === 0 ? (
                <Typography variant="caption">
                  {t("No stats available")}
                </Typography>
              ) : (
                <BarChartTab data={kindStsForBarGraph} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default DefaultEmployee;
